// src/utils/studentSpreadsheetMarking.js
// The student's side of interactive spreadsheet marking: their marked
// spreadsheet on the dashboard (StudentSpreadsheetView.jsx), to print and to
// download as an Excel file.
//
// Students can't read markingData (it holds marker-only things too), so when
// marking goes back to the student FirebaseMarker.jsx saves a copy of just
// the spreadsheet marking on the submission as `markedSpreadsheet` (see
// uploadMarkedPDF) - built by buildStudentSpreadsheetMarking below, in the
// same shape as the marking data, so the marking grid
// (SpreadsheetCellViewer.jsx) and the marked PDF pages
// (markedSpreadsheetPages.js) both take it as it is.

import { parseGrid, colLetter } from '../components/ExamSpreadsheetGrid';
import { evaluateGrid } from './spreadsheetFormulas';

const sheetKey = (cell) => `${cell.groupId}:${cell.gridKey}`;
const cellOnly = ({ groupId, gridKey, row, col }) => ({ groupId, gridKey, row, col });

/**
 * The spreadsheet marking a student may see, from the marker's marking data:
 * only what's on cells, only the fields the student's view needs. Comments
 * are numbered 1, 2, 3... within each spreadsheet.
 */
export function buildStudentSpreadsheetMarking(data) {
  const onCell = (list) => (list || []).filter(item => item && item.cell);
  const comments = onCell(data.numberedFeedback)
    .filter(f => f.text && f.text.trim())
    .sort((a, b) => a.number - b.number);
  const countBySheet = {};
  return {
    marks: onCell(data.marks).map(m => ({ id: m.id, type: m.type, cell: cellOnly(m.cell) })),
    numberedFeedback: comments.map(f => {
      const key = sheetKey(f.cell);
      countBySheet[key] = (countBySheet[key] || 0) + 1;
      return { id: f.id, number: countBySheet[key], text: f.text, cell: cellOnly(f.cell) };
    }),
    professionalMarks: onCell(data.professionalMarks).map(m => ({ id: m.id, markNumber: m.markNumber || null, reason: m.reason || '', cell: cellOnly(m.cell) })),
    additionalProfessionalMarks: onCell(data.additionalProfessionalMarks).map(m => ({ id: m.id, markNumber: m.markNumber || null, reason: m.reason || '', cell: cellOnly(m.cell) })),
    annotations: onCell(data.annotations).filter(a => a.type === 'highlight').map(a => ({ id: a.id, type: 'highlight', color: a.color, cell: cellOnly(a.cell) })),
    textStamps: onCell(data.textStamps).map(t => ({ id: t.id, label: t.label, cell: cellOnly(t.cell) })),
    spreadsheetColWidths: data.spreadsheetColWidths || {}
  };
}

/**
 * Every spreadsheet in the submission, in Spreadsheet 1, 2... order:
 * [{ groupId, gridKey, number, name, kind, gridJson, precomputedEvalJson }]
 */
export function listSpreadsheets(spreadsheetAnswers) {
  return Object.entries(spreadsheetAnswers || {})
    .flatMap(([groupId, group]) => [
      group.gridJson && { groupId, gridKey: 'spreadsheet', number: group.number, name: group.name || groupId, kind: group.blankGridJson ? 'prepopulated' : null, gridJson: group.gridJson, precomputedEvalJson: group.precomputedEvalJson },
      group.blankGridJson && { groupId, gridKey: 'blankSpreadsheet', number: group.blankNumber, name: group.name || groupId, kind: 'blank', gridJson: group.blankGridJson }
    ].filter(Boolean))
    .sort((a, b) => (a.number || Infinity) - (b.number || Infinity));
}

export const spreadsheetTitle = (sheet, index) => {
  const detail = [sheet.name, sheet.kind].filter(Boolean).join(', ');
  return `Spreadsheet ${sheet.number || index + 1} (${detail})`;
};

/** The marked spreadsheet pages on their own, as PDF bytes - for printing */
export async function markedSpreadsheetPdf(spreadsheetAnswers, marking) {
  const [{ PDFDocument, StandardFonts }, { addMarkedSpreadsheetPages }, { makeFontPdfSafe }] = await Promise.all([
    import('pdf-lib'),
    import('./markedSpreadsheetPages'),
    import('./pdfSafeText')
  ]);
  const pdfDoc = await PDFDocument.create();
  const font = makeFontPdfSafe(await pdfDoc.embedFont(StandardFonts.Helvetica));
  const boldFont = makeFontPdfSafe(await pdfDoc.embedFont(StandardFonts.HelveticaBold));
  // 'spreadsheet' - every spreadsheet, marked or not; with no pages of the
  // student's own to go after, they simply follow one another
  await addMarkedSpreadsheetPages({ pdfDoc, data: marking, spreadsheetAnswers, mode: 'spreadsheet', originalPages: [], font, boldFont });
  return pdfDoc.save();
}

const argb = (hex) => (/^#[0-9a-f]{6}$/i.test(hex || '') ? `FF${hex.slice(1).toUpperCase()}` : null);
const EXCEL_BORDER = { thin: 'thin', thick: 'thick', double: 'double' };

// What was marked on each cell, as lines of text: { 'r:c': [...] }
function markingByCell(marking, sheet) {
  const here = (cell) => cell.groupId === sheet.groupId && cell.gridKey === sheet.gridKey;
  const lines = {};
  const add = (cell, text) => { const key = `${cell.row}:${cell.col}`; (lines[key] = lines[key] || []).push(text); };
  (marking.marks || []).filter(m => here(m.cell)).forEach(m => add(m.cell, m.type === 'full-technical' ? '1 mark' : '½ mark'));
  (marking.numberedFeedback || []).filter(f => here(f.cell)).forEach(f => add(f.cell, `Comment ${f.number}: ${f.text}`));
  [...(marking.professionalMarks || []), ...(marking.additionalProfessionalMarks || [])].filter(m => here(m.cell))
    .forEach(m => add(m.cell, `P${m.markNumber || ''} professional mark${m.reason ? `: ${m.reason}` : ''}`));
  (marking.textStamps || []).filter(t => here(t.cell)).forEach(t => add(t.cell, t.label));
  return lines;
}

/**
 * The marked spreadsheets as an Excel workbook: one sheet per spreadsheet
 * with the student's own values, formulas and formatting, highlighted cells
 * in the marker's colours and the marking on each cell as an Excel note,
 * then a Feedback sheet listing all of it. ExcelJS only loads when this runs.
 * With no marking (null) it's just the student's own spreadsheets - their
 * copy from the exam's submitted screen (ExamWindowApp.jsx).
 */
export async function downloadMarkedSpreadsheetExcel(spreadsheetAnswers, marking, fileName) {
  marking = marking || {};
  const ExcelJSModule = await import('exceljs/dist/exceljs.min.js');
  const ExcelJS = ExcelJSModule.default || ExcelJSModule;
  const workbook = new ExcelJS.Workbook();
  const feedbackRows = [];
  const usedNames = new Set();

  listSpreadsheets(spreadsheetAnswers).forEach((sheet, index) => {
    let name = `Spreadsheet ${sheet.number || index + 1}`;
    while (usedNames.has(name)) name = `${name} `;
    usedNames.add(name);
    const ws = workbook.addWorksheet(name);

    const { grid, colWidths: studentWidths } = parseGrid(sheet.gridJson);
    let evaluated = null;
    if (sheet.precomputedEvalJson) {
      try { evaluated = JSON.parse(sheet.precomputedEvalJson); } catch (e) { evaluated = null; }
    }
    if (!evaluated) evaluated = evaluateGrid(grid);
    const markerWidths = marking.spreadsheetColWidths?.[`${sheet.groupId}:${sheet.gridKey}`];
    const colWidths = Array.isArray(markerWidths) && markerWidths.length === studentWidths.length ? markerWidths : studentWidths;
    ws.columns = colWidths.map(px => ({ width: Math.max(4, Math.round((px || 90) / 7)) }));

    const highlights = {};
    (marking.annotations || []).filter(a => a.cell.groupId === sheet.groupId && a.cell.gridKey === sheet.gridKey)
      .forEach(a => { highlights[`${a.cell.row}:${a.cell.col}`] = a.color; });
    const notes = markingByCell(marking, sheet);

    grid.forEach((row, r) => row.forEach((cell, c) => {
      const key = `${r}:${c}`;
      const raw = String(cell?.value ?? '');
      const value = evaluated[r]?.[c];
      if (!raw.trim() && !highlights[key] && !notes[key]) return;
      const target = ws.getCell(r + 1, c + 1);
      if (raw.trim()) {
        if (value?.isFormula) {
          target.value = { formula: raw.replace(/^=/, ''), result: value.isError ? undefined : (value.numeric ?? value.display) };
        } else if (value && value.numeric !== null && value.numeric !== undefined && !value.isError) {
          target.value = value.numeric;
        } else {
          target.value = raw;
        }
      }
      const font = {};
      if (cell.bold) font.bold = true;
      if (cell.italic) font.italic = true;
      if (cell.underline) font.underline = true;
      if (argb(cell.fontColor)) font.color = { argb: argb(cell.fontColor) };
      if (Object.keys(font).length) target.font = font;
      if (cell.align) target.alignment = { horizontal: cell.align };
      const fill = argb(highlights[key]) || argb(cell.fillColor);
      if (fill) target.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
      if (cell.borders && typeof cell.borders === 'object') {
        const border = {};
        ['top', 'bottom', 'left', 'right'].forEach(side => {
          if (EXCEL_BORDER[cell.borders[side]]) border[side] = { style: EXCEL_BORDER[cell.borders[side]] };
        });
        if (Object.keys(border).length) target.border = border;
      }
      if (notes[key]) {
        target.note = notes[key].join('\n');
        notes[key].forEach(text => feedbackRows.push([name, `${colLetter(c)}${r + 1}`, text]));
      }
    }));
  });

  if (feedbackRows.length) {
    const feedback = workbook.addWorksheet('Feedback');
    feedback.columns = [{ header: 'Spreadsheet', width: 16 }, { header: 'Cell', width: 8 }, { header: 'Marking', width: 90 }];
    feedback.getRow(1).font = { bold: true };
    feedbackRows.forEach(row => feedback.addRow(row));
    feedback.getColumn(3).alignment = { wrapText: true, vertical: 'top' };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
